import { Component, OnInit, Input, Output, EventEmitter, 
  ViewChild, Optional, Inject, HostListener } from '@angular/core';
import { FormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { AccountService, DistributorApiRow } from 'src/app/services/account.service';

import momentTimezones from 'moment-timezone';
import moment from 'moment';
import { PaymentService } from '../services/payment.service';
import { SubscriptionService } from '../services/subscription.service';
import { AccountPaymentComponent } from '../account-payment/account-payment.component';
import { AlertController, NavController, ToastController, Platform } from '@ionic/angular';
import { UserService } from 'src/app/services/user.service';
import { environment } from 'src/environments/environment';
import { ToggleService } from '../services/toggle.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-account-settings-general',
  templateUrl: './general.component.html',
  styleUrls: ['./general.component.scss'],
})
export class AccountSettingsGeneralComponent implements OnInit {
  @Output() pending = new EventEmitter<Object>();
  @Output() buttonText = new EventEmitter<Object>();
  @Output() changeSection = new EventEmitter<String>();

  public generalAccountInfoForm: UntypedFormGroup;
  public type;
  public numberKilns;
  public expiration; 
  public isExpired = false;
  public loading = false;
  public updatePending = false;
  private account_id;
  public payment;
  public paymentExpiredOrNonExistent = false;
  public formLoaded = false;
  existingSubscriptionId: string | false = false;
  public freeToPremium = this.isToggleEnabled("freeToPremium");

  distributorOptions: DistributorApiRow[] = [];
  distributorLoading = false;
  distributorSavePending = false;
  fieldLabelPlacement: 'stacked' | 'fixed' = 'fixed';
  
  @ViewChild('paymentsForm', { read: AccountPaymentComponent }) paymentForm;

  constructor(private accountService: AccountService,
    private paymentService: PaymentService,
    private subscriptionService: SubscriptionService,
    public alertController: AlertController,
    private toggleService: ToggleService,
    @Optional() @Inject('accountInformation') public accountInformation: any,
    @Optional() @Inject('canAdminister') public canAdminister: boolean,
    private userService: UserService,
    private navCtrl: NavController,
    private toastController: ToastController,
    private translate: TranslateService,
    private platform: Platform) {}

  isToggleEnabled(toggleName: string, userRole?: string): boolean {
    return this.toggleService.isToggleEnabled(toggleName, userRole);
  }

  /** Account owner may delete the KilnLink account (API enforces owner email). */
  canDeleteAccount(): boolean {
    if (!this.canAdminister || !this.account_id) return false;
    const ownerEmail = this.accountInformation?.account_owner?.email;
    const userEmail = this.userService.getLoginData()?.email;
    return !!(ownerEmail && userEmail && ownerEmail.toLowerCase() === userEmail.toLowerCase());
  }

  /** Account admins may configure weekly summary emails for the account. */
  canAccessWeeklySummarySettings(): boolean {
    return !!this.canAdminister;
  }

  isWeeklySummaryToggleEnabled(): boolean {
    return !!this.generalAccountInfoForm?.get('weekly_summary')?.value;
  }

  /** Explain weekly summary only when the admin can manage it and the toggle is on. */
  showWeeklySummaryOptions(): boolean {
    return this.canAccessWeeklySummarySettings() && this.isWeeklySummaryToggleEnabled();
  }

  trialAvailable(): boolean {
    return this.isToggleEnabled('freeToPremium');
  }

  accountExpired(): boolean {
    return this.userService.accountExpired();
  }

  /**
   * Premium trial / subscription end is today or within the next 30 days (renewal window).
   */
  isPremiumTrialExpirationWithinWindow(): boolean {
    if (!this.expiration) return false;
    const today = moment().startOf('day');
    const exp = moment(this.expiration).startOf('day');
    const daysUntilExpiration = exp.diff(today, 'days');
    return daysUntilExpiration >= 0 && daysUntilExpiration <= 30;
  }

  /** Trial accounts: Start Subscription only near trial end (same 30-day window). */
  shouldShowStartSubscription(): boolean {
    return (
      this.isToggleEnabled('freeToPremium') &&
      this.type === 'Trial' &&
      this.isPremiumTrialExpirationWithinWindow()
    );
  }

  /**
   * Cost row + payment UI: only when freeToPremium is on and subscription end is within 30 days (not expired).
   * Free accounts use Start Subscription / upgrade flows instead.
   */
  shouldShowCostPerYearAndPayment(): boolean {
    if (!this.isToggleEnabled('freeToPremium')) return false;
    if (!this.expiration || this.type === 'Free') return false;
    return this.isPremiumTrialExpirationWithinWindow();
  }

  startTrial(): void {
    void this.upgradeToTrial();
  }

  startSubscription(): void {
    void this.upgradeToTrial();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.syncFieldLabelPlacement();
  }

  private syncFieldLabelPlacement(): void {
    this.fieldLabelPlacement = this.platform.width() < 720 ? 'stacked' : 'fixed';
  }

  ngOnInit() {
    this.syncFieldLabelPlacement();
    this.loading = true;  
    if(this.accountInformation) {
      this.account_id = this.accountInformation.general.external_id;
      this.type = (this.accountInformation && this.accountInformation.general) ? this.accountInformation.general.type : 'N/A';
      this.numberKilns = (this.accountInformation && this.accountInformation.general) ? this.accountInformation.general.kiln_limit : 'N/A';
      this.formLoaded = false;
      this.createForm();

      let setUpFormWithoutSubscription = () => {
        this.createForm();
        let expiration = (this.accountInformation && this.accountInformation.general) ? this.accountInformation.general.expiration : null;
        if(expiration) this.isExpired = moment(new Date()).isAfter(moment(expiration));   
        this.expiration = moment(expiration).format('YYYY-MM-DD');
        this.paymentService.getPaymentInfo(this.account_id).then((payment_response) => {
          this.payment = payment_response.payment
          this.paymentExpiredOrNonExistent = !payment_response.payment ? true : false;
        });
      }

      if(this.isToggleEnabled("freeToPremium")) {
        this.subscriptionService.get(this.account_id).then((subscription) => {
          if(subscription) this.existingSubscriptionId = subscription['id']
          this.createForm(subscription);
          let expiration = subscription ? subscription['expirationDate'] : (this.accountInformation && this.accountInformation.general) ? this.accountInformation.general.expiration : null;
          this.expiration = moment(expiration).format('YYYY-MM-DD');
          if(expiration) {
            let now = moment(new Date())
            let exp = moment(expiration)
            this.isExpired = now.isAfter(exp);   
          }
                
          this.paymentService.getPaymentInfo(this.account_id).then((payment_response) => {
            this.payment = payment_response.payment
            this.paymentExpiredOrNonExistent = !payment_response.payment ? true : false;
          });    
        }).catch((error) => {
          console.log('error getting subscription', error);
          setUpFormWithoutSubscription();
        });
      } else {
        setUpFormWithoutSubscription();
      }

      void this.toggleService.listToggles().then(() => {
        this.loadDistributorsForSelect();
      });
    }
  }

  async loadDistributorsForSelect() {
    if (!this.canAdminister || !this.account_id || !this.isToggleEnabled('accountDistributor')) {
      return;
    }
    this.distributorLoading = true;
    try {
      this.distributorOptions = await this.accountService.listDistributorsForAccount(this.account_id);
    } catch (e) {
      console.error(e);
      this.distributorOptions = [];
    } finally {
      this.distributorLoading = false;
    }
  }

  distributorLabel(d: DistributorApiRow): string {
    const city = d.address?.city || '';
    const st = d.address?.state || '';
    const loc = [city, st].filter(Boolean).join(', ');
    return loc ? `${d.name} (${loc})` : d.name;
  }

  /** Persist distributor only (no full General form submit). Account admins can set or clear without Edit / payment validation. */
  async saveDistributorOnly() {
    if (!this.canAdminister || !this.isToggleEnabled('accountDistributor') || !this.account_id || !this.generalAccountInfoForm?.get('distributor_id')) {
      return;
    }
    const raw = this.generalAccountInfoForm.value.distributor_id;
    const distributorId =
      raw === '' || raw === undefined || raw === null ? null : Number(raw);
    this.distributorSavePending = true;
    try {
      await this.accountService.setAccountDistributor(this.account_id, distributorId);
      const fresh = await this.accountService.getAccountInformation();
      if (fresh && this.accountInformation) {
        this.accountInformation.distributor = fresh.distributor;
      }
      const t = await this.toastController.create({
        message: this.translate.instant('GENERAL.DISTRIBUTOR_SAVED'),
        duration: 2500,
        position: 'bottom',
        color: 'success',
      });
      await t.present();
    } catch (e) {
      const t = await this.toastController.create({
        message: this.translate.instant('GENERAL.DISTRIBUTOR_SAVE_FAILED'),
        duration: 3500,
        position: 'bottom',
        color: 'danger',
      });
      await t.present();
    } finally {
      this.distributorSavePending = false;
    }
  }

  async successfulUpdate() {
    this.updatePending = false;
    this.pending.emit({
      is_pending: false,
      message: 'Account general information updated.',
      success: true
    });
  }

  createForm(subscription = undefined) {
    if(!this.generalAccountInfoForm && this.accountInformation) {
      var guess_time_zone = momentTimezones.tz.guess();
      let account_has_time_zone = this.accountInformation && this.accountInformation.general && this.accountInformation.general.timezone
      let time_zone = (account_has_time_zone) ? this.accountInformation.general.timezone : guess_time_zone;
      let timezone = new UntypedFormControl(time_zone, [Validators.required]);

      let freq = 'kiln_annual'
      let diffFreq = 1;
      if(subscription) {
        freq = (subscription.planId || 'kiln_annual')      
        let difference = moment.duration(moment(subscription.paidThroughDate).diff(moment(subscription.firstBillingDate)))
        diffFreq = Math.round(freq.includes('annual') ? difference.asYears() : difference.asMonths())
      }
      
      const distId = this.accountInformation?.distributor?.id;
      const general = this.accountInformation?.general || {};
      const company = this.accountInformation?.company || {};
      this.generalAccountInfoForm = new UntypedFormGroup({
        type: new UntypedFormControl(this.type, [Validators.required]),
        numberControllers: new UntypedFormControl(this.numberKilns > 0 ? this.numberKilns : 1, [Validators.required, Validators.min(1)]),
        frequency: new UntypedFormControl(freq, [Validators.required]),
        renewals: new UntypedFormControl(diffFreq || 1, [Validators.required]),
        timezone: timezone,
        distributor_id: new UntypedFormControl(
          distId != null && distId !== undefined ? distId : ''
        ),
        sms_notifications_enabled: new UntypedFormControl(!!general.sms_notifications_enabled),
        email_alerts_enabled: new UntypedFormControl(!!general.email_alerts_enabled),
        push_notifications_enabled: new UntypedFormControl(
          general.push_notifications_enabled != null ? !!general.push_notifications_enabled : true
        ),
        weekly_summary: new UntypedFormControl(
          general.weekly_summary != null ? !!general.weekly_summary : !!company.weekly_summary
        ),
      });
      
      this.formLoaded = true;
    }
  }

  getTopUSTimezones() {
    return momentTimezones.tz.names().filter((name) => {
      let default_tzs = [
        'America/New_York', 'America/Chicago',
        'America/Denver', 'America/Los_Angeles',
        'America/Anchorage', 'Pacific/Honolulu'
      ]

      return default_tzs.includes(name);
    });
  }

  getTimezones() {
    return momentTimezones.tz.names();
  }

  public async save() {
    try {
      this.updatePending = true;
      if (this.paymentForm) await this.paymentForm.save();
      const formValue = this.generalAccountInfoForm.value;
      await this.accountService.updateGeneral(
        this.accountInformation.general.external_id,
        formValue.timezone,
        formValue.type,
        {
          sms_notifications_enabled: !!formValue.sms_notifications_enabled,
          email_alerts_enabled: !!formValue.email_alerts_enabled,
          push_notifications_enabled: !!formValue.push_notifications_enabled,
          weekly_summary: !!formValue.weekly_summary,
        }
      );
      if (this.accountInformation?.general) {
        this.accountInformation.general.sms_notifications_enabled = !!formValue.sms_notifications_enabled;
        this.accountInformation.general.email_alerts_enabled = !!formValue.email_alerts_enabled;
        this.accountInformation.general.push_notifications_enabled = !!formValue.push_notifications_enabled;
        this.accountInformation.general.weekly_summary = !!formValue.weekly_summary;
      }
      if (this.accountInformation?.company) {
        this.accountInformation.company.weekly_summary = !!formValue.weekly_summary;
      }        
      if(this.paymentService.getNonce()) {
        if(this.existingSubscriptionId) {
          await this.subscriptionService.update(this.account_id, this.existingSubscriptionId, this.generalAccountInfoForm.value)    
        } else {
          await this.subscriptionService.create(this.account_id, this.generalAccountInfoForm.value)    
        }        
      }
      if (this.canAdminister && this.isToggleEnabled('accountDistributor') && this.generalAccountInfoForm.get('distributor_id')) {
        const raw = this.generalAccountInfoForm.value.distributor_id;
        const distributorId =
          raw === '' || raw === undefined || raw === null ? null : Number(raw);
        await this.accountService.setAccountDistributor(this.account_id, distributorId);
        const fresh = await this.accountService.getAccountInformation();
        if (fresh && this.accountInformation) {
          this.accountInformation.distributor = fresh.distributor;
        }
      }
      this.successfulUpdate();
    } catch(e) {
      this.updatePending = false;
      this.pending.emit({
        is_pending: false,
        message: 'Sorry, we could not update your account information at this time',
        success: false
      });
    }
  }

  public async cancelSubscription() {
    if (!this.existingSubscriptionId) return;
    const alert = await this.alertController.create({
      header: this.translate.instant('GENERAL.CANCEL_SUBSCRIPTION_CONFIRM_HEADER'),
      message: this.translate.instant('GENERAL.CANCEL_SUBSCRIPTION_CONFIRM_MESSAGE'),
      buttons: [
        {
          text: this.translate.instant('GENERAL.KEEP_SUBSCRIPTION'),
          role: 'cancel',
          cssClass: 'secondary',
        },
        {
          text: this.translate.instant('GENERAL.CANCEL_SUBSCRIPTION'),
          handler: () => {
            void this.subscriptionService.delete(this.account_id, this.existingSubscriptionId);
          },
        },
      ],
    });
    await alert.present();
  }

  public async deleteAccount() {
    const accountName = this.accountInformation?.company?.name || '';
    const alert = await this.alertController.create({
      header: this.translate.instant('GENERAL.DELETE_ACCOUNT_CONFIRM_HEADER'),
      message: this.translate.instant('GENERAL.DELETE_ACCOUNT_CONFIRM_MESSAGE', { name: accountName }),
      buttons: [
        {
          text: this.translate.instant('GENERAL.CANCEL'),
          role: 'cancel',
          cssClass: 'secondary',
        },
        {
          text: this.translate.instant('USER_INFO.DELETE_ACCOUNT'),
          role: 'delete',
          cssClass: 'danger',
          handler: () => {
            void this.accountService
              .delete({ id: this.account_id, name: accountName })
              .then(() => this.navCtrl.navigateRoot('landing'))
              .catch(() => {
                void this.toastController
                  .create({
                    message: this.translate.instant('USER_INFO.DELETE_ACCOUNT_ERROR'),
                    duration: 3500,
                    position: 'bottom',
                    color: 'danger',
                  })
                  .then((t) => t.present());
              });
          },
        },
      ],
    });
    await alert.present();
  }

  public isValid() {
    const f = this.generalAccountInfoForm;
    const baseOk = !!(f && f.touched && f.valid);
    if (!baseOk) return false;
    if (this.type === 'Free') return true;
    if (!this.shouldShowCostPerYearAndPayment()) return true;
    return !!(this.paymentForm && this.paymentForm.isValid());
  }

  public isEditingPayment() {
    return this.paymentForm ? this.paymentForm.editing() : false;
  }

  public cancelEditing() {
    if (this.generalAccountInfoForm && this.accountInformation?.general) {
      const g = this.accountInformation.general;
      const tz = g.timezone || this.generalAccountInfoForm.value.timezone;
      this.generalAccountInfoForm.patchValue({
        type: g.type,
        timezone: tz,
      });
      this.type = g.type;
      this.generalAccountInfoForm.markAsPristine();
      this.generalAccountInfoForm.markAsUntouched();
    }
    this.syncDistributorFromAccount();
  }

  /** Reset distributor control from server state (e.g. after Cancel). */
  private syncDistributorFromAccount() {
    if (!this.generalAccountInfoForm?.get('distributor_id')) {
      return;
    }
    const id = this.accountInformation?.distributor?.id;
    this.generalAccountInfoForm.patchValue({
      distributor_id: id != null && id !== undefined ? id : '',
    });
  }

  startEditing(forcePayment = false) {
    if (forcePayment && this.paymentForm) {
      this.paymentForm.showPaymentForm();
    }
  }

  public async upgradeToTrial() {
    if (!this.isToggleEnabled('freeToPremium')) {
      return;
    }
    const alert = await this.alertController.create({
      header: this.translate.instant('ACCOUNT_ADMIN.TRIAL_UPGRADE_ALERT_TITLE'),
      message: this.translate.instant('ACCOUNT_ADMIN.TRIAL_UPGRADE_ALERT_MESSAGE'),
      buttons: [
        {
          text: this.translate.instant('GENERAL.CANCEL'),
          role: 'cancel',
          cssClass: 'secondary'
        },
        {
          text: this.translate.instant('ACCOUNT_ADMIN.TRIAL_UPGRADE_ALERT_CONFIRM'),
          handler: async () => {
            try {
              this.updatePending = true;
              const response = await this.accountService.upgradeAccount(this.account_id);
              
              if (response && response.success) {
                // Reload account information to ensure everything is in sync
                await this.accountService.getAccountInformation();
                this.accountInformation = this.accountService.accountInformation;
                
                // Update local account information
                if (this.accountInformation && this.accountInformation.general) {
                  this.type = this.accountInformation.general.type;
                  
                  // Update form
                  if (this.generalAccountInfoForm) {
                    this.generalAccountInfoForm.patchValue({ type: this.type });
                  }
                  
                  // Update expiration date
                  const expiration = this.accountInformation.general.expiration || this.accountInformation.general.subscription_end_date;
                  if (expiration) {
                    this.expiration = moment(expiration).format('YYYY-MM-DD');
                    this.isExpired = moment().isAfter(moment(expiration));
                  }
                }
                
                this.pending.emit({
                  is_pending: false,
                  message: 'Account upgraded to Trial successfully',
                  success: true
                });
              } else {
                this.pending.emit({
                  is_pending: false,
                  message: 'Failed to upgrade account',
                  success: false
                });
              }
            } catch (error) {
              console.error('Error upgrading account:', error);
              this.pending.emit({
                is_pending: false,
                message: 'Sorry, we could not upgrade your account at this time',
                success: false
              });
            } finally {
              this.updatePending = false;
            }
          }
        }
      ]
    });

    await alert.present();
  }
}
